/**
 * Cadastro de produto PRÓPRIO (2026-09-25) — constantes do front.
 *
 * `products` deixou de ser espelho do Bling: o cadastro é nosso. O Bling só
 * vincula `bling_id`, importa SKU inexistente e traz saldo; NF série 1 e
 * composição de kit continuam lá. Os limites abaixo espelham o contrato do
 * backend (`productWireSchemas.js` / `productCodes.js`) — mudar um lado exige
 * mudar o outro.
 *
 * Rótulos de PENDÊNCIA não moram aqui: vêm do `GET /products/pending` (o
 * catálogo é do servidor; o front não duplica).
 */

export const PRODUCT_TITLE_MAX = 120;
export const PRODUCT_SKU_MAX = 60;

/** Idêntica ao backend (`PRODUCT_SKU_PATTERN`): vale só para SKU NOVO. */
export const PRODUCT_SKU_PATTERN = /^[A-Z0-9](?:[A-Z0-9._/+-]{0,58}[A-Z0-9])?$/;

/** Prefixo dos SKUs que o sync antigo criava para produto sem código no Bling. */
export const PRODUCT_SKU_RESERVED_PREFIX = 'BID-';

export const PRODUCT_UNIT_MAX = 6;
/** Sugestões do datalist de Unidade (o campo aceita qualquer texto de até 6). */
export const PRODUCT_UNIT_OPTIONS = ['UN', 'PC', 'KG', 'CX', 'PCT'];
export const DEFAULT_PRODUCT_UNIT = 'UN';

/** `low_stock_threshold` vazio/null grava 20 (coluna NOT NULL). */
export const DEFAULT_LOW_STOCK_THRESHOLD = 20;
export const LOW_STOCK_THRESHOLD_MAX = 1_000_000;

/**
 * Sentinela de peso "não informado" (`NormalizeProductWeightKg(null)` do
 * backend grava 0,001). No editor ele aparece VAZIO e vira a pendência
 * `PESO_NAO_INFORMADO`.
 */
export const WEIGHT_SENTINEL_KG = 0.001;
export const WEIGHT_MAX_KG = 9_999_999.999;
export const WEIGHT_DECIMALS = 3;

export const SALE_PRICE_MAX = 9_999_999_999.99;
export const SALE_PRICE_DECIMALS = 2;
export const COST_PRICE_MAX = 99_999_999_999;
/** Custo por unidade do SKU com até 10 casas (`cost_price DECIMAL(21,10)`). */
export const COST_PRICE_DECIMALS = 10;

export const SUPPLIER_CODE_MAX = 120;
export const SUPPLIER_DESCRIPTION_MAX = 255;

export const UNITS_PER_CASE_MIN = 2;
export const UNITS_PER_CASE_MAX = 65_535;
export const UNITS_PER_PALLET_MIN = 1;
export const UNITS_PER_PALLET_MAX = 1_000_000;

/** Marca nova (org SUPPLIER): até 45 caracteres, maiúsculas pt-BR. */
export const BRAND_NAME_MAX = 45;

/** Tamanhos por campo: `ean` = GTIN do SKU (cEAN); `tax_ean` = cEANTrib; `case_gtin` = caixa do fornecedor. */
export const GTIN_LENGTHS = [8, 12, 13, 14];
export const TAX_GTIN_LENGTHS = [8, 12, 13];
export const CASE_GTIN_LENGTHS = [13, 14];

export const PRODUCT_STATUS_META = {
    ACTIVE: { label: 'Ativo', variant: 'success' },
    INACTIVE: { label: 'Inativo', variant: 'neutral' },
};

export const PRODUCT_FORMAT_LABELS = {
    SIMPLE: 'Simples',
    KIT: 'Kit',
};

export const PRODUCT_ORIGIN_LABELS = {
    OWN: 'Cadastro próprio',
    BLING_IMPORT: 'Importado do Bling',
};

/** Vínculo com o Bling, calculado no servidor (`ComputeBlingLinkStatus`). */
export const BLING_LINK_STATUS_META = {
    LINKED: {
        label: 'Vinculado',
        variant: 'success',
        help: 'Saldo vendável e espelho do WMS ativos',
    },
    NOT_LINKED: {
        label: 'Sem vínculo',
        variant: 'warning',
        help: 'Cadastre o mesmo SKU no Bling; o vínculo é automático em até 5 min',
    },
    NOT_SEEN: {
        label: 'Não listado pelo Bling',
        variant: 'danger',
        help: 'O Bling não listou este produto na última hora',
    },
};

/** Origem da mercadoria (ICMS), 0–8 — rótulos da tabela da NF-e. */
export const TAX_ORIGIN_OPTIONS = [
    { value: '0', label: '0 · Nacional, exceto as indicadas nos códigos 3, 4, 5 e 8' },
    { value: '1', label: '1 · Estrangeira - Importação direta, exceto a indicada no código 6' },
    { value: '2', label: '2 · Estrangeira - Adquirida no mercado interno, exceto a indicada no código 7' },
    { value: '3', label: '3 · Nacional, Conteúdo de Importação superior a 40% e inferior ou igual a 70%' },
    { value: '4', label: '4 · Nacional, produção conforme processos produtivos básicos' },
    { value: '5', label: '5 · Nacional, Conteúdo de Importação inferior ou igual a 40%' },
    { value: '6', label: '6 · Estrangeira - Importação direta, sem similar nacional, lista CAMEX' },
    { value: '7', label: '7 · Estrangeira - Adquirida no mercado interno, sem similar nacional, lista CAMEX' },
    { value: '8', label: '8 · Nacional, Conteúdo de Importação superior a 70%' },
];

/** Origens que levam Número FCI na NF (NT 2013.006). Espelha o backend. */
export const FCI_REQUIRED_ORIGINS = [3, 5, 8];
export const FCI_PATTERN = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;
